import type {
	ColDef,
	HeaderClassParams,
	ICellRendererParams,
	ValueFormatterFunc,
	ValueFormatterParams,
} from "ag-grid-community";
import { NUMBER_FILTER_OPTIONS } from "@/constants/grid/agGrid";
import type SearchGridSchema from "@/interfaces/SearchGridSchema";
import PillCellRenderer, { type PillCellRendererProps } from "./Pill";

/** Map a status string to a header class defined in index.css */
export const statusToHeaderClass = (status?: string): string => {
	switch (status) {
		case "HIGH":
			return "header-red-pill";
		case "MEDIUM":
			return "header-yellow-pill";
		case "LOW":
			return "header-green-pill";
		default:
			return "";
	}
};

/**
 * Map a status string to a pill class that styles data cells.
 */
export const statusToPillClass = (status?: string): string => {
	switch (status) {
		case "HIGH":
			return "pill-red";
		case "MEDIUM":
			return "pill-yellow";
		case "LOW":
			return "pill-green";
		default:
			return "pill-yellow"; // default neutral
	}
};

/**
 * Read a representative row from the grid API (first displayed row) and
 * extract the requested status field. Pure and testable.
 */
export const getRepresentativeStatus = (
	params: HeaderClassParams<SearchGridSchema, unknown>,
	statusField: keyof SearchGridSchema,
): string | undefined => {
	const firstRow = params?.api?.getDisplayedRowAtIndex?.(0) as
		| { data?: Partial<SearchGridSchema> }
		| undefined;

	const data = firstRow?.data;
	if (!data) return undefined;

	const value = (data as Record<string, unknown>)[statusField as string];
	return typeof value === "string" ? value : undefined;
};

/** Factory that creates a headerClass function for a given mapping between
 * a column and the status field that determines its color.
 */
export const headerClassFromStatusField = (statusField: keyof SearchGridSchema) => {
	return (params: HeaderClassParams<SearchGridSchema, unknown>): string | undefined => {
		const status = getRepresentativeStatus(params, statusField);
		return statusToHeaderClass(status) || undefined;
	};
};

/**
 * Create a small helper that returns a cellRendererParams object for pill renderer
 * based on a status field in the row data.
 */
export const pillParamsFromStatusField = (statusField: keyof SearchGridSchema) => {
	return (params: ICellRendererParams<SearchGridSchema, unknown>) => {
		const maybeStatus = params?.data
			? (params.data as Record<string, unknown>)[statusField as string]
			: undefined;
		const statusStr = typeof maybeStatus === "string" ? maybeStatus : undefined;

		// Use status string as label if present; otherwise fallback to numeric formatting if value exists
		let text = "N/A";
		if (typeof maybeStatus === "string") {
			text = maybeStatus;
		} else if (typeof params?.value === "number") {
			// reuse formatter with a shaped object
			const vfParams = params as unknown as ValueFormatterParams<unknown>;
			text = formatScoreValue(vfParams);
		}

		return { pillClass: statusToPillClass(statusStr), text };
	};
};

/**
 * Format numeric score values consistently for the grid.
 * Returns formatted string or readable placeholder values.
 */
export const formatScoreValue: ValueFormatterFunc = (
	params: ValueFormatterParams<unknown>,
): string => {
	const raw = params?.value as unknown;
	if (!params?.data) return "";

	if (raw === 0) return "0.00";
	if (raw === null || Number.isNaN(Number(raw))) return "Not Available";

	const n = Number(raw);
	if (n < 0 || n > 10) return "Not Applicable";

	return n.toFixed(2);
};

/**
 * Formatter for private label flags. Uses explicit string checks.
 */
export const formatPrivateLabel: ValueFormatterFunc = (
	params: ValueFormatterParams<unknown>,
): string => {
	const raw = params?.value as unknown;
	if (!params?.data) return "";

	if (raw === "NK") return "N";
	if (raw === "KS") return "Y";
	if (raw === null) return "Not Available";

	return "Not Available";
};

/**
 * Formatter for ISO date strings - returns MM/DD/YYYY or empty string when not present
 */
export const formatScoreDate: ValueFormatterFunc = (
	params: ValueFormatterParams<unknown>,
): string => {
	const raw = params?.value as unknown;
	if (raw === undefined || raw === null) return "";

	let d: Date | null = null;

	// If value is a Date instance, use it directly
	if (raw instanceof Date) {
		d = raw;
	} else if (typeof raw === "string") {
		const s = raw.trim();
		// Handle date-only strings like 'YYYY-MM-DD' explicitly to avoid timezone shifts.
		if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
			const [yStr, mStr, dStr] = s.split("-");
			const y = Number(yStr);
			const m = Number(mStr);
			const ddNum = Number(dStr);
			// Create local Date using year, month-1, day to prevent UTC shift
			d = new Date(y, m - 1, ddNum);
		} else {
			// Attempt to parse ISO-like strings (with timezone or timestamp)
			const parsed = new Date(s);
			d = Number.isNaN(parsed.getTime()) ? null : parsed;
		}
	} else {
		// unsupported type
		d = null;
	}

	if (!d || Number.isNaN(d.getTime())) return "";

	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	const yyyy = String(d.getFullYear());
	return `${mm}/${dd}/${yyyy}`;
};

/**
 * Safe wrapper for `formatScoreDate` that treats sentinel/epoch values as empty.
 * This is intended to be used directly as a column `valueFormatter`.
 */
export const safeFormatScoreDate: ValueFormatterFunc = (
	params: ValueFormatterParams<unknown>,
): string => {
	try {
		const raw = params?.value;
		// Treat common sentinel/empty values as "no value"
		if (raw == null) return "";
		if (typeof raw === "string") {
			const s = raw.trim();
			if (s === "" || s === "0000-00-00" || s.startsWith("1970-01-01")) return "";
		}

		// If raw is numeric 0 or string '0', treat as no value (epoch)
		if (raw === 0 || raw === "0") return "";

		// If raw is a Date object at epoch, treat as no value
		if (raw instanceof Date && raw.getTime && raw.getTime() === 0) return "";

		const formatted = formatScoreDate(params);

		// Also treat formatted epoch/date-shifted values as empty
		if (formatted === "01/01/1970" || formatted === "12/31/1969") return "";

		return formatted || "";
	} catch (e) {
		return "";
	}
};

/**
 * Create cellRendererParams for score columns that use pre-computed risk status from DB.
 * The database handles threshold logic based on isUsingDefault flag.
 */
export const createScorePillParamsFromStatus = (statusField: keyof SearchGridSchema) => {
	const perDimensionFields: Array<keyof SearchGridSchema> = [
		"transparencyRiskStatus",
		"environmentalRiskStatus",
		"humanRightsRiskStatus",
	];

	return (params: ICellRendererParams<SearchGridSchema, unknown>): PillCellRendererProps => {
		// Guard: ensure we have row data before attempting to render
		if (!(params && params.data)) {
			return { pillClass: "", text: "" };
		}

		const rawValue = params.value as number | undefined | null;

		// Handle missing numeric score
		if (rawValue === null || rawValue === undefined) {
			return { pillClass: "", text: "Not Available" };
		}

		// Ensure numeric range validity
		if (typeof rawValue !== "number" || rawValue < 0 || rawValue > 10) {
			return { pillClass: "", text: "Not Applicable" };
		}

		// Get the pre-computed status from the database
		const statusCandidate = params.data[statusField];
		const statusStr = typeof statusCandidate === "string" ? statusCandidate : undefined;

		const isPerDimension = perDimensionFields.includes(statusField);

		// For per-dimension score columns: only show red pill when status === 'HIGH'
		// For overall score: use the database-computed status to determine pill color
		const pillClass = isPerDimension
			? statusStr === "HIGH"
				? statusToPillClass("HIGH")
				: ""
			: statusToPillClass(statusStr);

		return { pillClass, text: rawValue.toFixed(2) };
	};
};

/**
 * Create cellRendererParams for the riskStatus column.
 * Uses pre-computed scoreRiskStatus from database.
 */
export const createRiskStatusPillParams = (
	params: ICellRendererParams<SearchGridSchema, unknown>,
): PillCellRendererProps => {
	if (!params.data) {
		return { pillClass: "", text: "" };
	}

	// Use the pre-computed status directly from the database
	const statusStr = params.data.scoreRiskStatus;
	const text = statusStr || "Not Available";
	const pillClass = statusToPillClass(statusStr);

	return { pillClass, text };
};

/**
 * Create cellRendererParams for the overallRiskScore column.
 * Uses pre-computed scoreRiskStatus from database for pill color.
 */
export const createOverallRiskScorePillParams = (
	params: ICellRendererParams<SearchGridSchema, unknown>,
): PillCellRendererProps => {
	if (!params.data) {
		return { pillClass: "", text: "" };
	}

	const formattedValue = formatScoreValue(params as unknown as ValueFormatterParams<unknown>);

	// Use the pre-computed status from the database for pill color
	const statusStr = params.data.scoreRiskStatus;
	const pillClass = statusToPillClass(statusStr);

	return { pillClass, text: formattedValue };
};

/**
 * Create the risk-related column definitions (riskStatus and overallRiskScore).
 * These columns use pre-computed scoreRiskStatus from the database.
 * The database handles conditional logic based on isUsingDefault flag.
 */
export const createRiskColumnDefs = (): ColDef<SearchGridSchema>[] => {
	const riskStatusCol: ColDef<SearchGridSchema> = {
		field: "scoreRiskStatus",
		headerName: "Risk\nStatus",
		minWidth: 150,
		cellRenderer: PillCellRenderer,
		cellRendererParams: createRiskStatusPillParams,
	};

	const overallRiskCol: ColDef<SearchGridSchema> = {
		field: "overallRiskScore",
		headerName: "Overall\nScore",
		minWidth: 150,
		filter: "agNumberColumnFilter",
		filterParams: {
			defaultOption: "equals",
			filterOptions: NUMBER_FILTER_OPTIONS,
		},
		valueFormatter: formatScoreValue,
		cellRenderer: PillCellRenderer,
		cellRendererParams: createOverallRiskScorePillParams,
	};

	return [riskStatusCol, overallRiskCol];
};
